/**
 * Slice LEVI-NAV-01 · Shared Levi navigation hook.
 *
 * One implementation used by every Levi entry point (§refinement 7).
 * Consumes a `navigation` payload emitted by /api/levi/command,
 * validates the destination_key against the fail-closed frontend
 * whitelist, protects unsaved work (§refinement 4), calls React
 * Router's navigate() with default history semantics
 * (§refinement 8 — back-button preserved), and reports the outcome
 * to the backend via /api/levi/navigation/ack so the session's
 * resolved_context updates only AFTER navigation actually completed
 * (§refinement 5).
 *
 * Failure isolation: any error in the ack path is logged but never
 * blocks the actual navigation the contractor requested.
 */
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  isSupportedDestination,
  resolveDestinationRoute,
} from "@/lib/leviNavDestinations";
import { readLeviWorkspaceSnapshot } from "@/lib/leviWorkspace";

const LOG_PREFIX = "[LEVI-NAV-01]";

function _confirmDiscardUnsaved(lang) {
  // Deterministic confirm — must not use Levi's voice to silently
  // decide. Contractor picks. Bilingual copy inline (no i18n dep
  // required to keep this hook portable across Levi entry points).
  const msg = lang === "es"
    ? "Tienes cambios sin guardar. ¿Descartarlos y continuar? (Cancelar mantiene la pantalla actual)"
    : "You have unsaved changes on this page. Discard them and continue? (Cancel keeps you here)";
  try {
    // eslint-disable-next-line no-alert
    return typeof window !== "undefined" && window.confirm ? window.confirm(msg) : true;
  } catch (_) {
    return true;
  }
}

export function useLeviNavigation() {
  const navigate = useNavigate();
  const inFlightRef = useRef(false);

  const performNavigation = useCallback(async (payload, opts = {}) => {
    const lang = opts.lang || "en";
    if (!payload || payload.action !== "navigate") {
      return { status: "ignored", reason: "not_navigation" };
    }
    const dk = payload.destination_key;
    if (!dk || !isSupportedDestination(dk)) {
      // Fail closed. Never construct arbitrary URLs.
      console.warn(LOG_PREFIX, "rejected unsupported destination_key:", dk);
      return { status: "rejected", reason: "unsupported_destination_key" };
    }
    const routeParams = payload.route_parameters || {};
    const route = resolveDestinationRoute(dk, routeParams);
    if (!route) {
      console.warn(LOG_PREFIX, "route resolution failed for", dk, routeParams);
      return { status: "rejected", reason: "route_resolution_failed" };
    }
    // R-25 · Optional prefill query string. Backend may attach a
    // whitelisted `query` object to be appended as ?key=value to the
    // resolved route so the frontend form can render prefilled
    // fields. Only string values are appended; nested objects are
    // dropped. This does NOT create a new navigation architecture —
    // it just carries a prefill hint through the existing
    // whitelisted route.
    let finalRoute = route;
    if (payload.query && typeof payload.query === "object") {
      const parts = [];
      for (const [k, v] of Object.entries(payload.query)) {
        if (v === null || v === undefined) continue;
        if (typeof v === "string" && v.length > 0) {
          parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
        }
      }
      if (parts.length > 0) {
        finalRoute = `${route}?${parts.join("&")}`;
      }
    }
    // Confidence gate (§refinement 3). "high" auto-navigates;
    // "moderate" auto-navigates only when we have workspace context
    // supporting the same target; "low" never reaches this hook
    // (backend returns clarify / not_found instead).
    const confidence = payload.confidence || "low";
    if (confidence === "low") {
      return { status: "rejected", reason: "low_confidence" };
    }
    // Unsaved-work protection (§refinement 4). Only ask when the
    // active document actually declares unsaved edits.
    const snapshot = readLeviWorkspaceSnapshot() || {};
    if (snapshot.has_unsaved_edits) {
      const proceed = _confirmDiscardUnsaved(lang);
      if (!proceed) {
        // Report the abort so the backend records the audit and
        // does NOT set resolved_context.
        if (opts.sessionId) {
          try {
            await api.post("/levi/navigation/ack", {
              session_id: opts.sessionId,
              destination_key: dk,
              route_parameters: routeParams,
              status: "cancelled_by_dirty_state",
            });
          } catch (e) {
            console.warn(LOG_PREFIX, "ack (dirty cancel) failed:", e);
          }
        }
        return { status: "cancelled_by_dirty_state" };
      }
    }
    if (inFlightRef.current) {
      // Double-click / rapid resubmit guard.
      return { status: "ignored", reason: "in_flight" };
    }
    inFlightRef.current = true;
    try {
      // Standard React Router history — preserves back-button
      // semantics (§refinement 8). No {replace: true}.
      navigate(finalRoute);
      // Ack the completed navigation so the session's
      // resolved_context is updated (§refinement 5). Failure here
      // does not undo the navigation.
      if (opts.sessionId) {
        try {
          await api.post("/levi/navigation/ack", {
            session_id: opts.sessionId,
            destination_key: dk,
            route_parameters: routeParams,
            project_id: (payload.project_context || {}).project_id || null,
            entity_type: payload.entity_type || null,
            entity_id: routeParams[
              (payload.route_parameters && Object.keys(payload.route_parameters)[0]) || ""
            ] || null,
            status: "completed",
          });
        } catch (e) {
          console.warn(LOG_PREFIX, "ack (completed) failed:", e);
        }
      }
      return { status: "completed", route, destination_key: dk };
    } finally {
      // Reset shortly after navigation kicks off so subsequent
      // Levi commands can navigate again immediately.
      setTimeout(() => { inFlightRef.current = false; }, 250);
    }
  }, [navigate]);

  return { performNavigation };
}
